/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
import {Injectable} from '@angular/core';
import {AppConfigService} from '../../../../common/service/app-config.service';
import {MapService} from '../../../services/map.service';
import {EnlargementUtilService} from './enlargement-util.service';
import {PlanModelService} from '../../../../common/service/plan-model.service';
import {SpatialUtilService} from '../../../services/spatial-util.service';
import {NgEventBus} from 'ng-event-bus';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class EnlargementSelectionService {
    constructor(
        private appConfigService: AppConfigService,
        private mapService: MapService,
        private enlargementUtilService: EnlargementUtilService,
        private planModelService: PlanModelService,
        private spatialUtilService: SpatialUtilService,
        private eventBus: NgEventBus
    ) {
        this.eventBus
            .on('selectFeaturesForEnlargement')
            .pipe(
                switchMap(meta => {
                    const {enlargementGraphic, callback} = meta.data as any;
                    return this.selectFeaturesForEnlargement(enlargementGraphic);
                })
            )
            .subscribe();
    }
    /**
     * Selects the features that contains within or intersecting an enlargement.
     */
    selectFeaturesForEnlargement(graphic): Observable<{annotations: any[]; angles: any[]}> {
        return new Observable(observer => {
            const selectedAnnotations = [];
            const selectedAngles = [];
            const selectedPoints = [];

            this.findFeaturesForSelection(
                this.spatialUtilService.getAnnotationMapLayer(),
                graphic.geometry.getExtent(),
                [
                    this.spatialUtilService.getLineMapLayer(),
                    this.spatialUtilService.getLotMapLayer(),
                    this.spatialUtilService.getInterestMapLayer(),
                    this.spatialUtilService.getRoadMapLayer(),
                    this.spatialUtilService.getPolygonMapLayer(),
                    this.spatialUtilService.getConstructLineMapLayer()
                ],
                selectedAnnotations,
                () => {
                    this.selectionQuery(this.spatialUtilService.getAnnotationMapLayer(), graphic.geometry, selectedAnnotations);
                    // ND-69193: filter points within enlargement and retrieve associated angles
                    this.selectionQuery(this.spatialUtilService.getPointMapLayer(), graphic.geometry, selectedPoints);
                    this.selectionAngleQuery(this.spatialUtilService.getAngleMapLayer(), selectedPoints, selectedAngles);

                    observer.next({annotations: selectedAnnotations, angles: selectedAngles});
                    observer.complete();
                }
            );
        });
    }
    /**
     * Finds the features for selection.
     */
    findFeaturesForSelection(layer: any, extent: any, layers: any[], selectedFeatures: any[], callback: any) {
        const intersectingFeatures = layers.reduce((acc, l) => {
            acc.push(
                ...this.spatialUtilService
                    .getGraphics(l)
                    .filter(
                        g =>
                            !(
                                l.id === 'polygonFeatureLayer' &&
                                g.attributes.type === this.appConfigService.getDesignProperties().SURROUND_POLYGON_TYPE
                            ) && extent.intersects(g.geometry)
                    )
            );
            return acc;
        }, []);

        const allAnnotations = this.spatialUtilService.getFeatures(layer);

        if (allAnnotations) {
            const annotationGroupMap = this.enlargementUtilService.getAnnotationGroupMap(allAnnotations);
            allAnnotations.forEach(annotation => {
                const annotationGroup = annotationGroupMap[this.enlargementUtilService.getAnnotationGroupKey(annotation)] || [annotation];

                if (
                    annotationGroup.some(annotationGroupItem => {
                        return (
                            extent.intersects(annotationGroupItem.geometry) ||
                            intersectingFeatures.some(intersectingFeature =>
                                this.enlargementUtilService.isAnnotationPropertyMatch(annotation, intersectingFeature, extent)
                            )
                        );
                    })
                ) {
                    selectedFeatures.push(annotation);
                }
            });
            callback();
        }
    }

    /**
     * Select live features for the given enlargement.
     */
    selectLineFeatures(graphic: any) {
        const lines = [];
        this.selectionQuery(this.spatialUtilService.getLineMapLayer(), graphic.geometry, lines);
        return lines;
    }

    /**
     * Runs query for the given layer and geometry, adding the result to the list of selected features.
     */
    selectionQuery(layer: any, polygonGeometry: any, selectedFeatures: any[]) {
        const features = this.spatialUtilService
            .getGraphics(layer)
            .filter(f => f.geometry && this.mapService.checkIntersect(polygonGeometry, f.geometry));
        selectedFeatures.push(...features);
    }

    /**
     * Find angle features associated with the given points.
     */
    selectionAngleQuery(angleLayer: any, pointSelected: any[], selectedFeatures: any[]) {
        const pointIds = pointSelected.map(f => f.attributes.id);

        const features = this.spatialUtilService.getGraphics(angleLayer).filter(fa => pointIds.includes(fa.attributes.pointid));
        selectedFeatures.push(...features);
    }
}
